import {
  AddressDto,
  CreateAddressForm,
  UpdateAddressForm,
} from '@act/shared/data-transfer-objects';
import { Control, FormState } from 'react-hook-form';
import { FormBuilder, FlexContainer, SelectOption } from '@act/storybook';
import STATE_OPTIONS from 'redux/constants/stateOptions';
import { FormConfig } from '../../../stories/forms/FormBuilder/formBuilder.types';

interface AddressContentProps {
  control: Control<CreateAddressForm | UpdateAddressForm>;
  formState: FormState<CreateAddressForm | UpdateAddressForm>;
  addresses?: AddressDto[];
  selectedAddress?: AddressDto;
  isEdit?: boolean;
  showDetails?: boolean;
}

export const AddressContent = ({ control, formState }: AddressContentProps) => {
  const stateOptions = STATE_OPTIONS;

  const formConfig: FormConfig<CreateAddressForm | UpdateAddressForm> = [
    {
      type: 'input',
      name: 'description',
      trackingName: 'description-input',
      label: 'Label',
      input: 'text',
      props: {
        placeholder: 'e.g., Home, Work, etc.',
        characterLimit: 20,
        required: true,
      },
    },
    {
      type: 'input',
      name: 'line1',
      trackingName: 'line1-input',
      label: 'Street Address',
      input: 'text',
      props: {
        required: true,
      },
    },
    {
      type: 'input',
      name: 'line2',
      trackingName: 'line2-input',
      label: 'Apartment, suite, etc.',
      input: 'text',
    },
    {
      type: 'input',
      name: 'city',
      trackingName: 'city-input',
      label: 'City',
      input: 'text',
      props: {
        required: true,
      },
    },
    [
      {
        type: 'input',
        name: 'state',
      trackingName: 'state-input',
        label: 'State',
        input: 'select',
        props: {
          options: Object.keys(stateOptions).map(
            key =>
              ({
                key: key,
                label: stateOptions[key],
                value: stateOptions[key],
              } as SelectOption<string>)
          ),
          required: true,
        },
      },
      {
        type: 'input',
        name: 'zip',
      trackingName: 'zip-input',
        label: 'ZIP Code',
        input: 'text',
        props: {
          required: true,
        },
      },
    ],
  ];

  return (
    <FlexContainer direction="column" gap={2}>
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    </FlexContainer>
  );
};

export const EditAddressContent = ({
  control,
  formState,
  addresses,
  selectedAddress,
  isEdit,
  showDetails,
}: AddressContentProps) => {
  function getDisplayAddress(address: AddressDto): string {
    const { description, line1 } = address;
    return description ? description : line1;
  }
  const formConfig: FormConfig<CreateAddressForm | UpdateAddressForm> = [
    {
      type: 'input',
      name: 'addressId',
      trackingName: 'address-id-input',
      label: 'Select Address',
      input: 'select',
      props: {
        options:
          addresses?.map(
            address =>
              ({
                key: address.id,
                label: getDisplayAddress(address),
                value: address.id,
              } as SelectOption<number>)
          ) || [],
        fullWidth: true,
      },
    },
  ];
  if (isEdit) {
    formConfig.push({
      type: 'input',
      name: 'description',
      trackingName: 'description-input',
      label: 'Label',
      input: 'text',
      disabled: !selectedAddress,
      props: {
        characterLimit: 20,
        required: true,
        placeholder: 'e.g., Home, Work, etc.',
      },
    });
  }
  if (isEdit || showDetails) {
    formConfig.push(
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'line1',
      trackingName: 'line1-input',
            label: 'Street Address',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
          {
            type: 'input',
            name: 'line2',
      trackingName: 'line2-input',
            label: 'Apartment, suite, etc.',
            input: 'text',
            disabled: showDetails || !selectedAddress,
          },
        ],
      },
      {
        type: 'group',
        items: [
          {
            type: 'input',
            name: 'city',
      trackingName: 'city-input',
            label: 'City',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
          {
            type: 'input',
            name: 'state',
      trackingName: 'state-input',
            label: 'State',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
          {
            type: 'input',
            name: 'zip',
      trackingName: 'zip-input',
            label: 'ZIP Code',
            input: 'text',
            disabled: showDetails || !selectedAddress,
            props: {
              required: true,
            },
          },
        ],
      }
    );
  }

  return (
    <FlexContainer direction="column" gap={2}>
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    </FlexContainer>
  );
};
